import React from "react";
import {
  B2BSpinner,
  TravelerSelectWorkflow,
  LoadingIndicator,
  TravelerSelectStep,
  useDeviceTypes,
  VoidWindowNotice,
  useCheckInMinimumAgeValidation,
  getAgeRequirementMessage,
  hasInvalidBirthdate,
} from "halifax";
import {
  IPerson,
  PersonId,
  CallState,
  SELECT_TRAVELERS,
  ITravelerStepErrors,
  CancellationReason,
} from "redmond";
import clsx from "clsx";
import { getConfigTenant, isCorpTenant, isVesperTenant } from "@capone/common";

import "./styles.scss";
import { PremierCollectionBookPassengerSelectionConnectorProps } from "./container";
import * as constants from "./textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { config } from "../../../../api/config";
import {
  AVAILABLE,
  HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";

export interface IPremierCollectionBookPassengerSelectionProps
  extends PremierCollectionBookPassengerSelectionConnectorProps {
  progress: TravelerSelectStep;
  setProgress: (progress: TravelerSelectStep) => void;
  onProgressChange?: (step: TravelerSelectStep) => void;
  onContinue?: (travelersChanged?: boolean) => void;
  onGoBack?: (travelersChanged?: boolean) => void;
  // Called when the picker is closed by anything other than its own Back or
  // Continue, e.g. browser back in the travel app moving the checkout step.
  onDismiss?: (travelersChanged: boolean) => void;
  className?: string;
  selectionScreenHeaderElement?: JSX.Element;
  onReviewStep?: boolean;
  isMobile?: boolean;
  disabled?: boolean;
  combinedStep?: boolean;
  showErrors?: ITravelerStepErrors;
  setShowErrors?: (showErrors: ITravelerStepErrors) => void;
  saveButtonClicked?: boolean;
  useLocalId?: boolean;
  travelerToEdit?: IPerson;
  setTravelerToEdit?: (traveler?: IPerson) => void;
  preselectedUserId?: string;
  useXmodHeadings?: boolean;
}

export const PremierCollectionBookPassengerSelection = (
  props: IPremierCollectionBookPassengerSelectionProps,
) => {
  const {
    className,
    travelers,
    progress,
    selectionScreenHeaderElement,
    setProgress,
    onProgressChange,
    onContinue,
    onGoBack,
    onDismiss,
    selectedTravelerId,
    setUserSelectedPassengerIds,
    updateUserPassenger,
    deleteUserPassenger,
    fetchUserPassengers,
    userPassengerCallState,
    onReviewStep,
    isMobile,
    disabled,
    combinedStep,
    showErrors,
    setShowErrors,
    saveButtonClicked,
    useLocalId,
    travelerToEdit,
    preselectedUserId,
    fetchCorpUserPassengers,
    cancellationSummary,
    isTravelApp,
    useXmodHeadings,
    minimumCheckInAge,
    checkInDate,
  } = props;
  const isVesper = isVesperTenant(getConfigTenant());

  const [localTravelerId, setLocalTravelerId] = React.useState<string[]>([]);
  const currentTravelerId = useLocalId
    ? localTravelerId[0]
    : selectedTravelerId;
  const travelersChanged = selectedTravelerId !== localTravelerId[0];
  const { matchesMobile } = useDeviceTypes();

  const expState = useExperiments();

  const californiaBill644Variant = getExperimentVariant(
    expState.experiments,
    HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  );
  const isCaliforniaBill644Experiment = React.useMemo(
    () => californiaBill644Variant === AVAILABLE,
    [californiaBill644Variant],
  );

  const showFree24HourCancel =
    isCaliforniaBill644Experiment &&
    cancellationSummary?.reasons.includes(CancellationReason.CaliforniaBill644);

  const { disabledTravelerIds, sortedTravelers, isTravelerEligible } =
    useCheckInMinimumAgeValidation({
      minimumAge: minimumCheckInAge,
      travelers,
      selectedTravelerId: currentTravelerId,
      setSelectedTravelerIds: (travelerIds: string[]) => {
        if (useLocalId) {
          setLocalTravelerId(travelerIds);
        }
        setUserSelectedPassengerIds({
          userSelectedPassengerIds: travelerIds,
        });
      },
      checkInDate,
    });

  React.useEffect(() => {
    isCorpTenant(config.TENANT)
      ? fetchCorpUserPassengers()
      : fetchUserPassengers();
  }, []);

  React.useEffect(() => {
    if (preselectedUserId) {
      setLocalTravelerId([preselectedUserId]);
    }
  }, [preselectedUserId]);
  React.useEffect(() => {
    if (
      travelers.length === 1 &&
      !disabledTravelerIds.includes(travelers[0].id) &&
      !hasInvalidBirthdate(travelers[0].dateOfBirth)
    ) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: [travelers[0].id],
      });
    }
  }, [travelers, disabledTravelerIds]);

  // Set when the picker closes itself (Back/Continue), so any other close can
  // be told apart and handled like Back.
  const closedByPicker = React.useRef(false);
  const previousProgress = React.useRef(progress);

  const handleContinue = () => {
    closedByPicker.current = true;
    if (useLocalId && travelersChanged) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: localTravelerId,
      });
    }
    onContinue && onContinue(travelersChanged);
  };

  const handleGoBack = () => {
    closedByPicker.current = true;
    if (useLocalId && travelersChanged) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: localTravelerId,
      });
    }
    onGoBack && onGoBack(travelersChanged);
  };

  React.useEffect(() => {
    const wasOpen = previousProgress.current !== TravelerSelectStep.Main;
    previousProgress.current = progress;
    if (progress !== TravelerSelectStep.Main) {
      closedByPicker.current = false;
      return;
    }
    if (!wasOpen || closedByPicker.current || !onDismiss) {
      return;
    }
    const hasPendingChange =
      !!useLocalId && localTravelerId.length > 0 && travelersChanged;
    if (hasPendingChange) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: localTravelerId,
      });
    }
    onDismiss(hasPendingChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to the picker closing only
  }, [progress]);

  const travelerInfoSubtitle = React.useMemo(() => {
    const baseSubtitle = matchesMobile
      ? constants.TRAVELER_INFO_SUBTITLE_MOBILE
      : constants.TRAVELER_INFO_SUBTITLE;

    if (minimumCheckInAge != null) {
      return `${baseSubtitle} ${getAgeRequirementMessage(minimumCheckInAge)}`;
    }

    return baseSubtitle;
  }, [matchesMobile, minimumCheckInAge]);

  return userPassengerCallState === CallState.InProcess ? (
    <LoadingIndicator
      indicatorSize="small"
      indicator={B2BSpinner}
      message={
        userPassengerCallState === CallState.InProcess
          ? constants.UPDATE_TEXT
          : constants.VALIDATE_TEXT
      }
    />
  ) : (
    <TravelerSelectWorkflow
      displayModalSubtitle
      showGenderField
      showNationalityField
      singleTravelerWorkflow
      buttonClassName="b2b"
      className={clsx(
        "premier-collection-book-passenger-selection-root",
        className,
        {
          "combined-step": combinedStep,
          redesigned: isVesper,
        },
      )}
      disabled={disabled}
      errorMessage={constants.ADD_TRAVELER_ERROR_MESSAGE}
      handleDeletePassenger={(travelerId: PersonId) => {
        if (useLocalId) {
          if (localTravelerId[0] === travelerId) {
            setLocalTravelerId([]);
          }
        }
        deleteUserPassenger({ personId: travelerId });
      }}
      handleUpdatePassenger={(traveler: IPerson) => {
        if (useLocalId && isTravelerEligible(traveler)) {
          setLocalTravelerId([traveler.id]);
        }
        updateUserPassenger(
          { person: traveler },
          traveler.driverLicense !== undefined,
        );
      }}
      isMobile={matchesMobile}
      onContinue={handleContinue}
      onGoBack={handleGoBack}
      onProgressChange={onProgressChange}
      progress={progress}
      saveButtonClicked={saveButtonClicked}
      selectedTravelerIds={currentTravelerId ? [currentTravelerId] : []}
      selectionScreenHeaderElement={selectionScreenHeaderElement}
      setProgress={setProgress}
      setSelectedTravelerIds={(travelerIds: string[]) => {
        trackEvent({
          eventName: SELECT_TRAVELERS,
          properties: {},
        });
        if (useLocalId) {
          setLocalTravelerId(travelerIds);
        } else {
          setUserSelectedPassengerIds({
            userSelectedPassengerIds: travelerIds,
          });
        }
      }}
      setShowErrors={setShowErrors}
      showErrors={showErrors}
      titles={{
        travelerInfoTitle: matchesMobile
          ? constants.TRAVELER_INFO_TEXT
          : constants.TRAVELER_INFO_TITLE_UPDATED,
        travelerInfoSubtitle,
        frequentFlyerTitle: constants.FREQUENT_FLYER_TITLE,
        additionalInfoTitle: constants.ADDITIONAL_INFO_TITLE,
        adultTitle: constants.ADULT_TITLE,
        childTitle: constants.CHILD_TITLE,
        infantSeatTitle: constants.INFANT_SEAT_TITLE,
        infantLapTitle: constants.INFANT_LAP_TITLE,
        addTravelers: matchesMobile
          ? onReviewStep
            ? constants.TRAVELER_INFO_TEXT
            : constants.ADD_TRAVELER_INFO_TEXT
          : constants.TRAVELER_INFO_TITLE_UPDATED,
      }}
      travelers={sortedTravelers}
      wrapButton={isMobile && !onReviewStep}
      editPassenger={travelerToEdit}
      tenant={config.TENANT}
      trackEvent={trackEvent}
      bottomContent={showFree24HourCancel ? <VoidWindowNotice /> : undefined}
      hideBackButton={isTravelApp}
      useXmodHeadings={useXmodHeadings}
      disabledTravelerIds={disabledTravelerIds}
    />
  );
};
